import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, ArrowDown, ArrowUp, Clock, Inbox, RefreshCw, Search,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import StatusBadge from '../../components/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import { getPosSale, normalizePosSalePayload } from '../../api/pos';
import {
  PAY_LATER_ERRORS,
  PAY_LATER_STATUS,
  listPayLaters,
} from '../../api/payLaters';
import { formatCurrency, timeAgo } from '../../data/mockData';
import { payLaterCashierNotes, payLaterMethodLines } from '../../utils/posMoney';
import PayLaterDrawer from './PayLaterDrawer';
import Receipt from '../POS/Receipt';
import styles from './PayLatersQueue.module.css';

const PAGE_SIZE = 100;

const TABS = [
  { key: 'pending', label: 'Pending', status: PAY_LATER_STATUS.PENDING },
  { key: 'collected', label: 'Paid', status: PAY_LATER_STATUS.COLLECTED },
  { key: 'all', label: 'All', status: 'ALL' },
];

const EMPTY_COPY = {
  pending: 'Pay later sales from POS will show up here until they are settled.',
  collected: 'Settled pay laters will appear here for reference.',
  all: 'No Pay Later bills match this filter.',
};

function friendlyError(err) {
  const code = err?.code || err?.message;
  return PAY_LATER_ERRORS[code] || err?.detail || err?.message || 'Something went wrong.';
}

function amountCopy(record) {
  const pending = record.status === PAY_LATER_STATUS.PENDING;
  const primary = formatCurrency(pending ? record.outstanding : record.total);
  let secondary = '';
  if (pending && record.amountPaid > 0) {
    secondary = `Paid ${formatCurrency(record.amountPaid)} of ${formatCurrency(record.total)}`;
  } else if (!pending) {
    const lines = payLaterMethodLines(record.paidByMethod);
    secondary = lines.length
      ? lines.map((line) => `${line.label} ${formatCurrency(line.amount)}`).join(' · ')
      : 'Paid in full';
  }
  return { primary, secondary };
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
}

function istDateKey(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function inDateRange(iso, fromDate, toDate) {
  const key = istDateKey(iso);
  if (!key) return !fromDate && !toDate;
  const start = fromDate && toDate && fromDate > toDate ? toDate : fromDate;
  const end = fromDate && toDate && fromDate > toDate ? fromDate : toDate;
  if (start && key < start) return false;
  if (end && key > end) return false;
  return true;
}

export default function PayLatersQueue() {
  const { user } = useAuth();
  const { notify } = useOrders();
  const [tab, setTab] = useState('pending');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortDir, setSortDir] = useState(null);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({ openBalance: 0, openCount: 0, creditLimit: null });
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [reprintReceipt, setReprintReceipt] = useState(null);
  const [autoPrintReceipt, setAutoPrintReceipt] = useState(false);
  const [collectChangeDue, setCollectChangeDue] = useState(0);
  const pageTopRef = useRef(null);

  const loadRecords = useCallback(async ({ cursor = null, append = false } = {}) => {
    if (!user?.idToken) return;
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError('');
    try {
      let pageCursor = append ? cursor : null;
      let items = [];
      let summary = null;
      let pages = 0;
      do {
        const data = await listPayLaters({
          status: tab,
          q: q || undefined,
          limit: PAGE_SIZE,
          cursor: pageCursor || undefined,
        }, user.idToken);
        items = items.concat(data.items);
        summary = data.summary;
        pageCursor = data.nextCursor;
        pages += 1;
      } while (!append && pageCursor && pages < 50);
      setRecords((prev) => (append ? [...prev, ...items] : items));
      setSummary(summary);
      setNextCursor(pageCursor);
    } catch (err) {
      setError(friendlyError(err));
      if (!append) setRecords([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [tab, q, fromDate, toDate, user?.idToken]);

  const effectiveSort = sortDir || (tab === 'pending' ? 'asc' : 'desc');
  const dateFiltered = Boolean(fromDate || toDate);
  const visibleRecords = useMemo(() => {
    const matched = records.filter((record) => inDateRange(record.createdAt, fromDate, toDate));
    return [...matched].sort((a, b) => {
      const at = new Date(a.createdAt || 0).getTime();
      const bt = new Date(b.createdAt || 0).getTime();
      return effectiveSort === 'asc' ? at - bt : bt - at;
    });
  }, [records, fromDate, toDate, effectiveSort]);

  const toggleSort = () => {
    setSortDir(effectiveSort === 'asc' ? 'desc' : 'asc');
  };

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setQ(qInput.trim());
    }, 280);
    return () => clearTimeout(handle);
  }, [qInput]);

  const switchTab = (key) => {
    setTab(key);
    setSortDir(null);
    setSelected(null);
  };

  const openReceipt = async (orderId, fallback = {}, { autoPrint = false, changeDue = 0 } = {}) => {
    const settledReceipt = fallback.receipt && typeof fallback.receipt === 'object' ? fallback.receipt : null;
    const hasLines = Array.isArray(settledReceipt?.line_items) && settledReceipt.line_items.length > 0;
    const data = hasLines
      ? { receipt: settledReceipt, sale: fallback.sale }
      : await getPosSale(orderId, user.idToken);
    const payload = normalizePosSalePayload(data, {
      order_id: orderId,
      display_id: fallback.displayId || fallback.display_id,
      cashier_name: user?.name,
    });
    const typed = String(fallback.cashierNote || '').trim();
    if (typed && !payLaterCashierNotes(payload).includes(typed)) {
      payload.cashier_notes = [...payLaterCashierNotes(payload), typed];
    }
    setCollectChangeDue(changeDue);
    setAutoPrintReceipt(autoPrint);
    setReprintReceipt(payload);
  };

  const handlePaid = async (updated) => {
    setSelected(null);
    if (!updated) {
      loadRecords();
      return;
    }
    const via = updated.paidVia || updated.settlementMethod || '';
    const change = Number(updated.changeDue) || 0;
    const due = Number(updated.outstanding) || 0;
    const collected = updated.status === PAY_LATER_STATUS.COLLECTED || due <= 0;
    notify(collected
      ? `Paid in full${via ? ` via ${via}` : ''}${change > 0 ? ` · change ${formatCurrency(change)}` : ''}`
      : `Collected${via ? ` via ${via}` : ''} · ${formatCurrency(due)} still due`);
    loadRecords();
    const orderId = updated.orderId || updated.order_id || updated.id;
    if (!orderId) return;
    try {
      await openReceipt(orderId, updated, { autoPrint: false, changeDue: change });
    } catch (err) {
      notify(friendlyError(err));
    }
  };

  const handleReprint = async (record) => {
    try {
      await openReceipt(record.orderId || record.id, record, { autoPrint: false, changeDue: 0 });
    } catch (err) {
      notify(friendlyError(err));
    }
  };

  return (
    <div ref={pageTopRef}>
      <PageHeader
        eyebrow="Operations"
        title="Pay Laters"
        subtitle="Store-wide open credit — collect dues and reprint slips."
        actions={(
          <Button variant="outline" size="sm" onClick={() => loadRecords()} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </Button>
        )}
      />

      <div className={styles.summaryRow}>
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>Open dues</span>
          <strong>{summary.openCount}</strong>
        </div>
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>Open balance</span>
          <strong>{formatCurrency(summary.openBalance)}</strong>
        </div>
      </div>

      <div className={styles.filterRow}>
        <div className={styles.searchRow}>
          <Search size={14} />
          <input
            type="search"
            placeholder="Phone, name, bill id…"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            aria-label="Search pay laters"
          />
        </div>
        <div className={styles.dateFilters}>
          <label className={styles.dateField}>
            <span>From</span>
            <input
              type="date"
              value={fromDate}
              max={toDate || undefined}
              onChange={(e) => setFromDate(e.target.value)}
              aria-label="From date"
            />
          </label>
          <label className={styles.dateField}>
            <span>To</span>
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => setToDate(e.target.value)}
              aria-label="To date"
            />
          </label>
          <button type="button" className={styles.sortBtn} onClick={toggleSort}>
            {effectiveSort === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
            {effectiveSort === 'asc' ? 'Oldest first' : 'Newest first'}
          </button>
          {dateFiltered && (
            <button
              type="button"
              className={styles.clearDates}
              onClick={() => {
                setFromDate('');
                setToDate('');
              }}
            >
              Clear dates
            </button>
          )}
        </div>
        <div className={styles.filterTabs}>
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`${styles.filterTab} ${tab === key ? styles.filterTabActive : ''}`}
              onClick={() => switchTab(key)}
            >
              {label}
              {key === 'pending' && summary.openCount > 0 && (
                <span className={styles.tabCount}>{summary.openCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className={styles.loadingText}>Loading pay laters…</p>}

      {!loading && error && (
        <EmptyState
          icon={AlertTriangle}
          title="Could not load pay laters"
          description={error}
          action={<Button variant="outline" onClick={() => loadRecords()}>Retry</Button>}
        />
      )}

      {!loading && !error && visibleRecords.length === 0 && (
        <EmptyState
          icon={Inbox}
          title={dateFiltered
            ? 'No pay laters in this date range'
            : `No ${TABS.find((t) => t.key === tab)?.label.toLowerCase() || ''} pay laters`}
          description={dateFiltered
            ? 'Try a wider range, or clear the dates to see every bill.'
            : EMPTY_COPY[tab]}
        />
      )}

      {!loading && !error && visibleRecords.length > 0 && (
        <div className={styles.listArea}>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Society</th>
                  <th>Block / Flat</th>
                  <th>Due</th>
                  <th>
                    <button type="button" className={styles.whenSort} onClick={toggleSort}>
                      When
                      {effectiveSort === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                    </button>
                  </th>
                  <th>Status</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {visibleRecords.map((r) => {
                  const amounts = amountCopy(r);
                  return (
                  <tr key={r.orderId || r.id}>
                    <td>
                      <div className={styles.customerCell}>
                        <div className={styles.customerIcon}><Clock size={16} /></div>
                        <div className={styles.customerMeta}>
                          <p className={styles.customerName}>{r.customer?.fullName || '—'}</p>
                          <p className={styles.customerPhone}>
                            {r.customer?.phone ? `+91 ${r.customer.phone}` : '—'}
                          </p>
                          {r.customer?.addressLine1 && (
                            <p className={styles.customerAddress}>{r.customer.addressLine1}</p>
                          )}
                          <p className={styles.customerAddress}>{r.displayId}</p>
                        </div>
                      </div>
                    </td>
                    <td>{r.customer?.addressLine2 || '—'}</td>
                    <td>
                      {[r.customer?.block, r.customer?.flat].filter(Boolean).join(' / ') || '—'}
                    </td>
                    <td className={styles.amountCell}>
                      {amounts.primary}
                      {amounts.secondary && (
                        <span className={styles.amountSub}>{amounts.secondary}</span>
                      )}
                    </td>
                    <td className={styles.timeCell} title={formatDate(r.createdAt)}>
                      {timeAgo(r.createdAt)}
                    </td>
                    <td><StatusBadge status={r.status} size="sm" /></td>
                    <td className={styles.actionsCell}>
                      <div className={styles.rowActions}>
                        <Button variant="outline" size="sm" onClick={() => handleReprint(r)}>
                          Reprint
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setSelected(r)}>
                          {r.status === PAY_LATER_STATUS.PENDING ? 'Collect' : 'View'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={styles.mobileList} aria-label="Pay laters">
            {visibleRecords.map((r) => {
              const amounts = amountCopy(r);
              return (
              <article key={r.orderId || r.id} className={styles.mobileCard}>
                <div className={styles.mobileCardTop}>
                  <div className={styles.customerCell}>
                    <div className={styles.customerIcon}><Clock size={16} /></div>
                    <div className={styles.customerMeta}>
                      <p className={styles.customerName}>{r.customer?.fullName || '—'}</p>
                      <p className={styles.customerPhone}>
                        {r.customer?.phone ? `+91 ${r.customer.phone}` : '—'}
                      </p>
                      {r.customer?.addressLine1 && (
                        <p className={styles.customerAddress}>{r.customer.addressLine1}</p>
                      )}
                    </div>
                  </div>
                  <StatusBadge status={r.status} size="sm" />
                </div>
                <div className={styles.mobileMetaRow}>
                  <span>{r.customer?.addressLine2 || '—'}</span>
                  <strong className={styles.amountCell}>
                    {amounts.primary}
                    {amounts.secondary && (
                      <span className={styles.amountSub}>{amounts.secondary}</span>
                    )}
                  </strong>
                </div>
                {r.status === PAY_LATER_STATUS.COLLECTED && r.paidAt && (
                  <p className={styles.paidMeta}>
                    Last payment {formatDate(r.paidAt)}
                  </p>
                )}
                <div className={styles.mobileCardFooter}>
                  <span className={styles.timeCell} title={formatDate(r.createdAt)}>
                    {timeAgo(r.createdAt)}
                  </span>
                  <div className={styles.rowActions}>
                    <Button variant="outline" size="sm" onClick={() => handleReprint(r)}>
                      Reprint
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setSelected(r)}>
                      {r.status === PAY_LATER_STATUS.PENDING ? 'Collect' : 'View'}
                    </Button>
                  </div>
                </div>
              </article>
              );
            })}
          </div>

          {nextCursor && !dateFiltered && (
            <div className={styles.paginationBar}>
              <Button
                variant="outline"
                size="sm"
                disabled={loadingMore}
                onClick={() => loadRecords({ cursor: nextCursor, append: true })}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </div>
      )}

      <PayLaterDrawer
        record={selected}
        token={user?.idToken}
        onClose={() => setSelected(null)}
        onPaid={handlePaid}
        onReprint={handleReprint}
      />

      {reprintReceipt && (
        <Receipt
          key={`${reprintReceipt.order_id || ''}-${autoPrintReceipt ? 'print' : 'view'}`}
          mode="reprint"
          autoPrint={autoPrintReceipt}
          changeDue={collectChangeDue}
          receipt={reprintReceipt}
          token={user.idToken}
          closeLabel="Close"
          onClose={() => {
            setReprintReceipt(null);
            setAutoPrintReceipt(false);
            setCollectChangeDue(0);
          }}
        />
      )}
    </div>
  );
}
